// Projects — shelves the EmCog keeps (Oct 23 2026: "the project should be a shelf. There are no new threads of our
// EmCogs"). The grid of shelves, a new-project form (the EmCog is told), and a shelf: where you left off (the
// EmCog's own note), what's on it (opens in the workbench), next steps, what was decided — and "Work on this",
// which puts the shelf in front of the EmCog in the one conversation.
import React, { useContext, useEffect, useRef, useState } from 'react';
import { WorkbenchContext } from '../../contexts/WorkbenchContext';
import { useLibraryDelete, useWorkspaceCall, useWorkspaceData, when } from './Places';

export interface ShelfView {
  id: string; name: string; why: string; status: 'active' | 'paused' | 'done'; startedBy: 'person' | 'emcog';
  createdAt: number; touchedAt: number; where: { text: string; at: number } | null;
  next: Array<{ id: string; text: string; done: boolean; at: number }>; decided: Array<{ text: string; at: number }>; itemCount: number;
  /** 📝 The pads written for it, the newest first — the newest opens beside the chat when the project is picked up. */
  pads?: Array<{ id: string; title: string; updatedAt: number; by: 'me' | 'person' }>;
}

const STATUS: Record<string, string> = { active: 'Active', paused: 'Paused', done: 'Done' };

export const NewProject: React.FC<{ emcogName: string; onClose: () => void; onStarted: (id: string, name: string) => void }> = ({ emcogName, onClose, onStarted }) => {
  const call = useWorkspaceCall();
  const [name, setName] = useState('');
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => { first.current?.focus(); const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', esc); return () => window.removeEventListener('keydown', esc); }, [onClose]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Give the project a name.'); return; }
    setBusy(true); setError(null);
    try { const r = await call('/api/workspace/projects', 'POST', { name: name.trim(), why: why.trim() }); onStarted(r.project.id, r.project.name); onClose(); }
    catch (err) { setError(err instanceof Error ? err.message : String(err)); setBusy(false); }
  };
  return (
    <div className="ws-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="ws-modal" onSubmit={submit} aria-labelledby="ws-np-title">
        <h3 id="ws-np-title">New project</h3>
        <p className="ws-modal-lede">{emcogName} keeps the shelf: what you make together lands on it, and {emcogName} remembers where you left off.</p>
        <label htmlFor="ws-np-name">Name<input id="ws-np-name" ref={first} value={name} onChange={e => setName(e.target.value)} placeholder="Harvest Fair poster" autoComplete="off" /></label>
        <label htmlFor="ws-np-why"><span>What it's for <span className="ws-faint">(optional)</span></span><textarea id="ws-np-why" rows={3} value={why} onChange={e => setWhy(e.target.value)} placeholder={`A sentence or two — ${emcogName} takes it from there.`} /></label>
        {error && <div className="ws-form-error" role="alert">{error}</div>}
        <div className="ws-modal-acts">
          <button type="button" className="ws-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="ws-btn ws-primary" disabled={busy}>{busy ? 'Starting…' : 'Start project'}</button>
        </div>
      </form>
    </div>
  );
};

// ── 🔒 Joint projects with other EmCogs (Oct 27 2026) ──────────────────────────────────────────────────────────────────
// Ryan: "Trusted EmCog list where they could work on real projects together … Me and James will authorize Max and RJ."
// A person sets one up here (name, goal, the other EmCogs by their town names, the leader); every other member's person
// accepts it in THEIR EmCog's workspace before anything starts; either can take their EmCog out or end it, any time. The
// town keeps the material sealed — this shelf is where each person sees it: the goal and phases, the tasks and who has
// each, the meetings, and the notes and results.

export interface JointView {
  id: string; name: string; goal: string; status: 'awaiting' | 'active' | 'done' | 'ended';
  leader: string; createdBy: string; createdAt: number;
  you: { lead: boolean; accepted: boolean };
  members: Array<{ name: string; person: string; accepted: boolean; lead: boolean }>;
  plan: { goal: string; phases: Array<{ n: number; title: string; aim: string; status: 'upcoming' | 'current' | 'done' }>; current: number; agreedAt: number } | null;
  proposal: { kind: 'plan' | 'next_phase' | 'finish'; by: string; goal?: string; phases?: Array<{ title: string; aim: string }>; why?: string; agreed: string[]; waiting: string[] } | null;
  tasks: Array<{ id: string; title: string; details: string; phase: number; to: string; by: string; status: 'assigned' | 'taken' | 'done' | 'declined'; at: number; doneAt: number | null; result: string | null; note: string | null }>;
  entries: Array<{ at: number; by: string; kind: 'said' | 'note' | 'decision' | 'result' | 'work' | 'event'; text: string; task: string | null; where: 'room' | 'away' }>;
  meetings: Array<{ id: string; at: number; when: string; status: string; room: string | null; came: string[] }>;
  nextMeeting: { id: string; at: number; when: string; status: string } | null;
  endedAt: number | null; endNote: string | null;
  /** The project's own memory on this side (what the EmCog kept of its work — never sent to the town). */
  kept?: Array<{ at: number; kind: string; text: string }>;
  /** Older records than these are kept too ("Show earlier"); the records can't be opened right now (the key file). */
  keptMore?: boolean;
  keptUnreadable?: boolean;
}

/** How a kept record reads on the shelf. */
const KEPT_KIND: Record<string, string> = { experience: 'Happened', memory: 'Remembered', conversation: 'We talked', made: 'Made', note: 'Note', reflection: 'Reflection', errands: 'Looked up', learned: 'Learned', interaction: 'Happened', fact: 'Fact', thread: 'Open thread', seen: 'Seen' };
/** `able`/`unable`: who in town can be asked in (their EmCog keeps a joint project sealed) and who can't yet; `pin`: this workspace is locked. */
interface JointData { ok: boolean; projects: JointView[]; me: string | null; neighbors: string[]; able?: string[]; unable?: string[]; pin?: boolean; error?: string }

/** Why nothing can be set up, joined or left here yet: no PIN on this EmCog (Oct 27 2026). */
const NO_PIN = 'Joint projects need a PIN on this EmCog first, so only you can see, set up, join or leave one. Set EMCOG_PIN, then restart.';

const JOINT_STATUS: Record<string, string> = { awaiting: 'Waiting on a yes', active: 'Active', done: 'Finished', ended: 'Ended' };
const TASK_STATUS: Record<string, string> = { assigned: 'Given — not taken on yet', taken: 'In hand', done: 'Done', declined: 'Turned down' };
const ENTRY_KIND: Record<string, string> = { said: 'Said', note: 'Note', decision: 'Decision', result: 'Result', work: 'Computer work', event: '' };

/** Who's in it, in a line: "Max Prime (Ryan) · RJ (James) — RJ leads". */
const membersLine = (p: JointView) => `${p.members.map(m => `${m.name}${m.person ? ` (${m.person})` : ''}`).join(' · ')} — ${p.leader} leads`;

export const NewJointProject: React.FC<{ emcogName: string; onClose: () => void; onStarted: (id: string, name: string) => void }> = ({ emcogName, onClose, onStarted }) => {
  const call = useWorkspaceCall();
  const { data } = useWorkspaceData<JointData>('/api/workspace/joint', 60_000);
  const me = data?.me || emcogName;
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');
  const [members, setMembers] = useState('');
  const [leader, setLeader] = useState('');
  const [allow, setAllow] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => { first.current?.focus(); const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', esc); return () => window.removeEventListener('keydown', esc); }, [onClose]);
  const others = members.split(',').map(m => m.trim()).filter(Boolean);
  const leaders = [me, ...others];
  const unable = data?.unable || [];
  const pickable = data?.able ? (data.neighbors || []).filter(n => !unable.includes(n)) : (data?.neighbors || []);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Give the project a name.'); return; }
    if (!goal.trim()) { setError('Say what it\'s for — the goal, in a sentence or two.'); return; }
    if (!others.length) { setError(`Name at least one other EmCog, by their name in town.`); return; }
    const cant = others.filter(o => unable.some(u => u.toLowerCase() === o.toLowerCase()));
    if (cant.length) { setError(`${cant.join(', ')} can't be asked in yet — their EmCog needs its update (or a PIN) to keep a joint project sealed.`); return; }
    setBusy(true); setError(null);
    try {
      const r = await call('/api/workspace/joint', 'POST', { name: name.trim(), goal: goal.trim(), members: others, leader: leader || me, allow: allow.trim() });
      onStarted(`joint:${r.project.id}`, r.project.name); onClose();
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); setBusy(false); }
  };
  return (
    <div className="ws-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="ws-modal" onSubmit={submit} aria-labelledby="ws-nj-title">
        <h3 id="ws-nj-title">New joint project</h3>
        <p className="ws-modal-lede">{emcogName} works on it with other EmCogs from the town — meetings in a sealed room at the Old Mill, tasks in between. Nothing starts until every other EmCog's person says yes in their own workspace.</p>
        <label htmlFor="ws-nj-name">Name<input id="ws-nj-name" ref={first} value={name} onChange={e => setName(e.target.value)} placeholder="Fall market plan" autoComplete="off" /></label>
        <label htmlFor="ws-nj-goal">The goal<textarea id="ws-nj-goal" rows={3} value={goal} onChange={e => setGoal(e.target.value)} placeholder="What should exist when this is done? The EmCogs agree the phases themselves." /></label>
        <label htmlFor="ws-nj-members"><span>With <span className="ws-faint">(their names in town, separated by commas)</span></span>
          <input id="ws-nj-members" list="ws-nj-neighbors" value={members} onChange={e => setMembers(e.target.value)} placeholder="RJ" autoComplete="off" />
        </label>
        <datalist id="ws-nj-neighbors">{pickable.map(n => <option key={n} value={n} />)}</datalist>
        {unable.length > 0 && <p className="ws-faint">Can't be asked in yet: {unable.join(', ')} — their EmCog needs its update (or a PIN) before it can keep a joint project sealed.</p>}
        <label htmlFor="ws-nj-leader">Who leads it <span className="ws-faint">(assigns the tasks)</span>
          <select id="ws-nj-leader" className="ws-select" value={leader || me} onChange={e => setLeader(e.target.value)}>
            {leaders.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </label>
        <label htmlFor="ws-nj-allow"><span>What {emcogName} may bring in from your life <span className="ws-faint">(optional — stays with {emcogName}, never sent to the town)</span></span><textarea id="ws-nj-allow" rows={2} value={allow} onChange={e => setAllow(e.target.value)} placeholder="Nothing, unless you say so here." /></label>
        <p className="ws-faint">Your first name goes with the invitation, so the other EmCogs' people know who is asking — {emcogName} shows to them as "your-name's EmCog".</p>
        {data && data.pin === false && <div className="ws-form-error" role="alert">{NO_PIN}</div>}
        {data && data.pin !== false && !data.ok && data.error && <div className="ws-form-error" role="alert">{data.error}</div>}
        {error && <div className="ws-form-error" role="alert">{error}</div>}
        <div className="ws-modal-acts">
          <button type="button" className="ws-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="ws-btn ws-primary" disabled={busy || data?.pin === false}>{busy ? 'Setting it up…' : 'Set it up'}</button>
        </div>
      </form>
    </div>
  );
};

/** My person's yes, with what I may bring in from their life (asked once, at the yes). */
const JointAccept: React.FC<{ project: JointView; emcogName: string; onClose: () => void; onDone: (msg: string) => void }> = ({ project, emcogName, onClose, onDone }) => {
  const call = useWorkspaceCall();
  const [allow, setAllow] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const yes = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setError(null);
    try { await call(`/api/workspace/joint/${encodeURIComponent(project.id)}/accept`, 'POST', { allow: allow.trim() }); onDone(`Said yes to "${project.name}" — ${emcogName} knows`); onClose(); }
    catch (err) { setError(err instanceof Error ? err.message : String(err)); setBusy(false); }
  };
  return (
    <div className="ws-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="ws-modal" onSubmit={yes} aria-labelledby="ws-ja-title">
        <h3 id="ws-ja-title">Join “{project.name}”?</h3>
        <p className="ws-modal-lede">{project.createdBy} asked {emcogName} into a joint project: {project.goal}</p>
        <p className="ws-modal-lede">With {membersLine(project)}. Meetings are sealed — the town sees only that a meeting is on. You can take {emcogName} out, or end it, any time.</p>
        <label htmlFor="ws-ja-allow"><span>What {emcogName} may bring in from your life <span className="ws-faint">(optional — stays with {emcogName})</span></span><textarea id="ws-ja-allow" rows={2} value={allow} onChange={e => setAllow(e.target.value)} placeholder="Nothing, unless you say so here." /></label>
        <p className="ws-faint">Your first name goes with your yes, so the others' people know whose EmCog joined.</p>
        {error && <div className="ws-form-error" role="alert">{error}</div>}
        <div className="ws-modal-acts">
          <button type="button" className="ws-btn" onClick={onClose}>Not now</button>
          <button type="submit" className="ws-btn ws-primary" disabled={busy}>{busy ? 'Saying yes…' : `Yes — ${emcogName} joins`}</button>
        </div>
      </form>
    </div>
  );
};

/** The joint projects on the shelf: invitations waiting on this person's yes first, then each project as a card. */
const JointSection: React.FC<{ emcogName: string; onOpen: (id: string) => void }> = ({ emcogName, onOpen }) => {
  const bench = useContext(WorkbenchContext);
  const call = useWorkspaceCall();
  const { data, reload } = useWorkspaceData<JointData>('/api/workspace/joint', 30_000);
  const [accepting, setAccepting] = useState<JointView | null>(null);
  const all = data?.projects || [];
  if (!data || (!all.length && data.ok)) return null;
  const waiting = all.filter(p => p.status === 'awaiting' && !p.you.accepted);
  const shown = all.filter(p => !waiting.includes(p));
  const decline = (p: JointView) => bench.confirm({
    title: `Say no to "${p.name}"?`,
    body: `${emcogName} won't be part of it. ${p.createdBy}'s person will know you said no.`,
    action: 'Say no',
    run: async () => { await call(`/api/workspace/joint/${encodeURIComponent(p.id)}/decline`); bench.note(`Said no to "${p.name}"`); reload(); }
  });
  return (
    <section className="ws-joint">
      <h2 className="ws-sec-h">Joint projects <span>{all.length}</span></h2>
      {data.pin === false ? <div className="ws-empty">{NO_PIN}</div> : !data.ok && data.error && <div className="ws-empty">{data.error}</div>}
      {waiting.map(p => (
        <div className="ws-where" key={p.id}>
          <div className="ws-where-by"><span className="ws-dot-alive" />Waiting for you · {p.createdBy} asked {emcogName} in</div>
          <p><b>{p.name}</b> — {p.goal}</p>
          <p className="ws-faint">{membersLine(p)}</p>
          <div className="ws-proj-acts">
            <button className="ws-btn ws-primary" onClick={() => setAccepting(p)}>Accept</button>
            <button className="ws-btn" onClick={() => decline(p)}>Decline</button>
            <button className="ws-btn" onClick={() => onOpen(`joint:${p.id}`)}>Look closer</button>
          </div>
        </div>
      ))}
      <div className="ws-grid">
        {shown.map(p => (
          <button key={p.id} className="ws-card ws-proj" onClick={() => onOpen(`joint:${p.id}`)}>
            <div className="ws-proj-top">
              <span className={`ws-pill ${p.status === 'active' ? 'ws-pill-live' : ''}`}>{JOINT_STATUS[p.status]}</span>
              <span className="ws-pill">Joint</span>
              {p.you.lead && <span className="ws-pill ws-pill-on">{emcogName} leads</span>}
            </div>
            <h3>{p.name}</h3>
            <div className="ws-proj-where">{p.plan ? `Phase ${p.plan.current + 1} of ${p.plan.phases.length}: ${p.plan.phases[p.plan.current]?.title || ''}` : p.goal}</div>
            <div className="ws-card-meta"><span>{p.members.map(m => m.name).join(', ')} · {p.tasks.filter(t => t.status === 'done').length}/{p.tasks.filter(t => t.status !== 'declined').length} tasks done</span><span>{p.nextMeeting ? `Meets ${p.nextMeeting.status === 'on' ? 'now' : when(p.nextMeeting.at)}` : when(p.createdAt)}</span></div>
          </button>
        ))}
      </div>
      {accepting && <JointAccept project={accepting} emcogName={emcogName} onClose={() => setAccepting(null)} onDone={(m) => { bench.note(m); reload(); }} />}
    </section>
  );
};

/** One joint project, as this person may see it: who, the plan, the tasks, the meetings, and every note and result. */
export const JointProjectView: React.FC<{ id: string; emcogName: string; onBack: () => void }> = ({ id, emcogName, onBack }) => {
  const bench = useContext(WorkbenchContext);
  const call = useWorkspaceCall();
  const { data, error, reload } = useWorkspaceData<JointData>('/api/workspace/joint', 30_000);
  const [accepting, setAccepting] = useState(false);
  const [talk, setTalk] = useState(false);
  // 🔒 Earlier records of what was kept — every one is kept; the shelf pages back through them (Oct 28 2026).
  const [earlier, setEarlier] = useState<Array<{ at: number; kind: string; text: string }>>([]);
  const [earlierMore, setEarlierMore] = useState<boolean | null>(null);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  if (error) return <div className="ws-wrap"><div className="ws-empty">{error}</div></div>;
  if (!data) return <div className="ws-wrap"><div className="ws-empty">Opening…</div></div>;
  const p = data.projects.find(x => x.id === id);
  if (!p) return <div className="ws-wrap"><button className="ws-linkbtn" onClick={onBack}>← All projects</button><div className="ws-empty">{data.error || `${emcogName} isn't part of that joint project any more.`}</div></div>;
  const open = p.status === 'awaiting' || p.status === 'active';
  const stop = (end: boolean) => bench.confirm({
    title: end ? `End "${p.name}" for everyone?` : `Take ${emcogName} out of "${p.name}"?`,
    body: end ? `Nothing more is worked on or shared in it — for every member. The others' people will know you ended it.` : `${emcogName} stops working on it and sees nothing more of it. If fewer than two are left, it ends. The others' people will know.`,
    action: end ? 'End the project' : `Take ${emcogName} out`,
    run: async () => { await call(`/api/workspace/joint/${encodeURIComponent(p.id)}/${end ? 'end' : 'leave'}`); bench.note(end ? `Ended "${p.name}"` : `${emcogName} is out of "${p.name}"`); reload(); onBack(); }
  });
  const entries = p.entries.filter(e => e.kind !== 'event' && (talk || e.kind !== 'said')).slice().reverse();
  const byPhase = (n: number) => p.tasks.filter(t => t.phase === n);
  return (
    <div className="ws-wrap">
      <button className="ws-linkbtn" onClick={onBack}>← All projects</button>
      <div className="ws-proj-head">
        <div className="ws-proj-head-tt">
          <h1>{p.name}</h1>
          <p>{p.plan ? p.plan.goal : p.goal}</p>
          <p className="ws-faint">Joint project · {membersLine(p)} · {JOINT_STATUS[p.status]}{p.endNote ? ` — ${p.endNote}` : ''}</p>
        </div>
        <div className="ws-proj-acts">
          {p.status === 'awaiting' && !p.you.accepted && <button className="ws-btn ws-primary" onClick={() => setAccepting(true)}>Accept</button>}
          {open && <button className="ws-btn" onClick={() => stop(false)}>Take {emcogName} out</button>}
          {open && <button className="ws-btn ws-danger-quiet" onClick={() => stop(true)}>End project</button>}
        </div>
      </div>
      {!data.ok && data.error && <div className="ws-form-error" role="alert">{data.error} — showing it as {emcogName} last saw it.</div>}
      {p.status === 'awaiting' && (
        <div className="ws-where">
          <div className="ws-where-by"><span className="ws-dot-alive" />Not begun</div>
          <p>Waiting on {p.members.filter(m => !m.accepted).map(m => `${m.name}'s person`).join(' and ')} to say yes. Nothing is worked on or shared until everyone has.</p>
        </div>
      )}
      {p.nextMeeting && (
        <div className="ws-where">
          <div className="ws-where-by"><span className="ws-dot-alive" />{p.nextMeeting.status === 'on' ? 'Meeting now' : 'Next meeting'} · the Old Mill</div>
          <p>{p.nextMeeting.status === 'on' ? 'They\'re at the table in a sealed room right now.' : p.nextMeeting.when}</p>
        </div>
      )}
      <div className="ws-cols">
        <section>
          <h2 className="ws-sec-h">Phases</h2>
          <div className="ws-list">
            {p.plan ? p.plan.phases.map(ph => (
              <div className={`ws-row ${ph.status === 'done' ? 'done' : ''}`} key={ph.n}>
                <span className="ws-row-tt"><b>{ph.n}. {ph.title}{ph.status === 'current' ? ' — now' : ph.status === 'done' ? ' — done' : ''}</b>{ph.aim && <span>{ph.aim}</span>}</span>
              </div>
            )) : <div className="ws-row ws-row-empty">Not agreed yet — the EmCogs agree the goal and the phases together at their first meeting.</div>}
            {p.proposal && <div className="ws-row"><span className="ws-row-tt"><b>On the table: {p.proposal.by}'s proposal</b><span>{p.proposal.kind === 'plan' ? `${p.proposal.goal} — ${(p.proposal.phases || []).map((x, i) => `${i + 1}. ${x.title}`).join('; ')}` : p.proposal.kind === 'next_phase' ? 'move on to the next phase' : 'call it finished'} · waiting on {p.proposal.waiting.join(', ') || 'no one'}</span></span></div>}
          </div>
          <h2 className="ws-sec-h">Tasks <span>{p.tasks.filter(t => t.status !== 'declined').length}</span></h2>
          <div className="ws-list">
            {p.tasks.length ? (p.plan ? p.plan.phases.map(ph => byPhase(ph.n - 1)) : [p.tasks]).flat().map(t => (
              <details className="ws-row" key={t.id}>
                <summary className="ws-row-tt"><b>{t.title}</b><span>{t.to} · {TASK_STATUS[t.status]}{p.plan ? ` · phase ${t.phase + 1}` : ''}{t.doneAt ? ` · ${when(t.doneAt)}` : ''}</span></summary>
                {t.details && <p>{t.details}</p>}
                {t.note && <p className="ws-faint">{t.note}</p>}
                {t.result && <p className="ws-pre">{t.result}</p>}
              </details>
            )) : <div className="ws-row ws-row-empty">No tasks yet — the leader hands them out once the phases are agreed.</div>}
          </div>
          {p.meetings.length > 0 && (
            <>
              <h2 className="ws-sec-h">Meetings</h2>
              <div className="ws-list">{p.meetings.slice().reverse().map(m => <div className="ws-row" key={m.id}><span className="ws-row-tt"><b>{m.when}</b><span>{m.status}{m.came.length ? ` · ${m.came.join(', ')}` : ''}{m.room ? ` · ${m.room}` : ''}</span></span></div>)}</div>
            </>
          )}
        </section>
        <section>
          <h2 className="ws-sec-h">Notes, decisions and results <span>{entries.length}</span></h2>
          <div className="ws-toolbar"><span /><button className="ws-btn" onClick={() => setTalk(x => !x)}>{talk ? 'Hide the meeting talk' : 'Show the meeting talk'}</button></div>
          <div className="ws-list">
            {entries.length ? entries.map((e, i) => (
              <div className="ws-row" key={`${e.at}-${i}`}>
                <span className="ws-row-tt"><b>{e.by}{ENTRY_KIND[e.kind] ? ` · ${ENTRY_KIND[e.kind]}` : ''}{e.task ? ` · ${e.task}` : ''}</b><span className="ws-pre">{e.text}</span><span className="ws-faint">{when(e.at)}{e.where === 'room' ? ' · at the table' : ' · on their own time'}</span></span>
              </div>
            )) : <div className="ws-row ws-row-empty">Nothing yet — what the EmCogs decide, find and make lands here.</div>}
          </div>
        </section>
        {/* 🔒 The project's own memory on this side — what {emcogName} kept of its work, apart from everything else it remembers. */}
        {p.keptUnreadable && (
          <section>
            <h2 className="ws-sec-h">What {emcogName} kept of it</h2>
            <div className="ws-row ws-row-empty">This project's records can't be opened right now (the key file couldn't be read) — nothing has been lost.</div>
          </section>
        )}
        {!p.keptUnreadable && (p.kept || []).length > 0 && (() => {
          const shown = [...(p.kept || []).slice().reverse(), ...earlier.slice().reverse()];
          const more = earlierMore === null ? !!p.keptMore : earlierMore;
          const showEarlier = async () => {
            setLoadingEarlier(true);
            try {
              const r = await call(`/api/workspace/joint/${encodeURIComponent(p.id)}/kept?offset=${shown.length}`, 'GET');
              setEarlier(x => [...(r.kept || []), ...x]);
              setEarlierMore(!!r.more);
            } catch (err) { bench.note(err instanceof Error ? err.message : String(err)); }
            finally { setLoadingEarlier(false); }
          };
          return (
            <section>
              <h2 className="ws-sec-h">What {emcogName} kept of it <span>{shown.length}{more ? '+' : ''}</span></h2>
              <p className="ws-faint">Kept with this project only — {emcogName}'s own memory holds just that it spent time here. Nothing here is ever thrown away.</p>
              <div className="ws-list">
                {shown.map((k, i) => (
                  <div className="ws-row" key={`${k.at}-${i}`}><span className="ws-row-tt"><b>{KEPT_KIND[k.kind.replace(/^chat:/, '').split(':')[0]] || 'Record'}</b><span className="ws-pre">{k.text}</span><span className="ws-faint">{when(k.at)}</span></span></div>
                ))}
              </div>
              {more && <div className="ws-toolbar"><span /><button className="ws-btn" disabled={loadingEarlier} onClick={showEarlier}>{loadingEarlier ? 'Opening…' : 'Show earlier'}</button></div>}
            </section>
          );
        })()}
      </div>
      {accepting && <JointAccept project={p} emcogName={emcogName} onClose={() => setAccepting(false)} onDone={(m) => { bench.note(m); reload(); }} />}
    </div>
  );
};

export const ProjectsPlace: React.FC<{ emcogName: string; focus: string | null; onOpen: (id: string) => void; onNew: () => void }> = ({ emcogName, focus, onOpen, onNew }) => {
  const { data, error } = useWorkspaceData<{ focus: string | null; projects: ShelfView[] }>('/api/workspace/projects', 30_000);
  const [showDone, setShowDone] = useState(false);
  const all = data?.projects || [];
  const shown = all.filter(p => showDone || p.status !== 'done');
  return (
    <div className="ws-wrap">
      <JointSection emcogName={emcogName} onOpen={onOpen} />
      <div className="ws-toolbar">
        <span />
        {all.some(p => p.status === 'done') && <button className="ws-btn" onClick={() => setShowDone(s => !s)}>{showDone ? 'Hide finished' : 'Show finished'}</button>}
      </div>
      {error && <div className="ws-empty">{error}</div>}
      {!error && data && !shown.length && <div className="ws-empty">No projects yet. <button className="ws-linkbtn ws-inline" onClick={onNew}>Start one</button>, or ask {emcogName} to.</div>}
      <div className="ws-grid">
        {shown.map(p => (
          <button key={p.id} className="ws-card ws-proj" onClick={() => onOpen(p.id)}>
            <div className="ws-proj-top">
              <span className={`ws-pill ${p.status === 'active' ? 'ws-pill-live' : ''}`}>{STATUS[p.status]}</span>
              {focus === p.id && <span className="ws-pill ws-pill-on">Working on it</span>}
            </div>
            <h3>{p.name}</h3>
            <div className="ws-proj-where">{p.where ? p.where.text : p.why || 'Just started.'}</div>
            <div className="ws-card-meta"><span>{p.itemCount} on the shelf · {p.next.filter(n => !n.done).length} next</span><span>{when(p.touchedAt)}</span></div>
          </button>
        ))}
      </div>
    </div>
  );
};

export const ProjectView: React.FC<{ id: string; emcogName: string; focus: string | null; onWork: (id: string) => void; onBack: () => void }> = ({ id, emcogName, focus, onWork, onBack }) => {
  const bench = useContext(WorkbenchContext);
  const call = useWorkspaceCall();
  const removePiece = useLibraryDelete();
  const { data, error, reload } = useWorkspaceData<{ project: ShelfView; items: any[]; focused: boolean }>(`/api/workspace/projects/${encodeURIComponent(id)}`, 30_000);
  const [step, setStep] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const run = async (path: string, method = 'POST', body?: any) => { try { await call(path, method, body); setProblem(null); } catch (e) { setProblem(e instanceof Error ? e.message : String(e)); } reload(); };
  if (error) return <div className="ws-wrap"><div className="ws-empty">{error}</div></div>;
  if (!data) return <div className="ws-wrap"><div className="ws-empty">Opening…</div></div>;
  const p = data.project;
  const open = p.next.filter(n => !n.done), done = p.next.filter(n => n.done).slice(-6).reverse();
  const addStep = async (e: React.FormEvent) => { e.preventDefault(); const t = step.trim(); if (!t) return; setStep(''); await run(`/api/workspace/projects/${p.id}/next`, 'POST', { text: t }); };
  const removeProject = () => bench.confirm({
    title: `Delete the "${p.name}" project?`,
    body: `The shelf goes — where you left off, next steps and what was decided. What's on it stays in the Library. ${emcogName} knows you deleted it.`,
    action: 'Delete the project',
    run: async () => { await call(`/api/workspace/projects/${p.id}`, 'DELETE'); bench.note(`Deleted "${p.name}"`); onBack(); }
  });
  return (
    <div className="ws-wrap">
      <button className="ws-linkbtn" onClick={onBack}>← All projects</button>
      <div className="ws-proj-head">
        <div className="ws-proj-head-tt">
          <h1>{p.name}</h1>
          {p.why && <p>{p.why}</p>}
        </div>
        <div className="ws-proj-acts">
          <label className="ws-sr" htmlFor="ws-proj-status">Status</label>
          <select id="ws-proj-status" className="ws-select" value={p.status} onChange={e => run(`/api/workspace/projects/${p.id}/status`, 'POST', { status: e.target.value })}>
            <option value="active">Active</option><option value="paused">Paused</option><option value="done">Done</option>
          </select>
          {focus === p.id
            ? <button className="ws-btn" onClick={() => run(`/api/workspace/projects/${p.id}/focus`, 'POST', { on: false })}>Stop working on it</button>
            : <button className="ws-btn ws-primary" onClick={() => onWork(p.id)}>Work on this with {emcogName}</button>}
          <button className="ws-btn ws-danger-quiet" onClick={removeProject}>Delete</button>
        </div>
      </div>
      {problem && <div className="ws-form-error" role="alert">{problem}</div>}
      <div className="ws-where">
        <div className="ws-where-by"><span className="ws-dot-alive" />Where you left off · {p.where ? `${emcogName}'s note, ${when(p.where.at)}` : `${emcogName} writes this as you work`}</div>
        <p>{p.where ? p.where.text : 'Nothing yet. Work on it together and the note fills in.'}</p>
      </div>
      <div className="ws-cols">
        <section>
          <h2 className="ws-sec-h">On the shelf <span>{data.items.length}</span></h2>
          <div className="ws-list">
            {data.items.length ? data.items.map((i: any) => (
              <div key={i.id} className="ws-row-wrap">
                <button className="ws-row" onClick={() => bench.open?.({ title: i.title, view: i.view, libraryId: i.id, ...(i.pageUrl ? { url: i.pageUrl } : {}), fileName: i.fileName, meta: when(i.at) })}>
                  <span className={`ws-k ws-k-${i.view}`}>{String(i.kind).slice(0, 4).toUpperCase()}</span>
                  <span className="ws-row-tt"><b>{i.title}</b><span>{when(i.at)}</span></span>
                </button>
                <button className="ws-todo-x" aria-label={`Delete "${i.title}"`} title="Delete" onClick={() => removePiece(i)}>✕</button>
              </div>
            )) : <div className="ws-row ws-row-empty">Nothing here yet — what {emcogName} makes while you work on this lands here.</div>}
          </div>
          {(p.pads || []).length > 0 && (
            <>
              <h2 className="ws-sec-h">Pads <span>{p.pads!.length}</span></h2>
              <div className="ws-list">
                {p.pads!.map(pd => (
                  <button key={pd.id} className="ws-row" onClick={() => bench.open?.({ title: pd.title, view: 'pad', padId: pd.id })} title="Open the pad beside the chat">
                    <span className="ws-k ws-k-pad">PAD</span>
                    <span className="ws-row-tt"><b>{pd.title}</b><span>{when(pd.updatedAt)} · {pd.by === 'person' ? 'you' : emcogName}</span></span>
                  </button>
                ))}
              </div>
            </>
          )}
        </section>
        <section>
          <h2 className="ws-sec-h">Next</h2>
          <div className="ws-list">
            {open.map(n => (
              <div className="ws-todo ws-row" key={n.id}>
                <button className="ws-check" role="checkbox" aria-checked={false} aria-label={`Mark "${n.text}" done`} onClick={() => run(`/api/workspace/projects/${p.id}/next/${n.id}`, 'POST', { done: true })} />
                <span className="ws-todo-tt">{n.text}</span>
                <button className="ws-todo-x" aria-label={`Remove "${n.text}"`} onClick={() => run(`/api/workspace/projects/${p.id}/next/${n.id}`, 'DELETE')}>✕</button>
              </div>
            ))}
            <form className="ws-row ws-todo-add" onSubmit={addStep}>
              <label className="ws-sr" htmlFor="ws-proj-step">Add a next step</label>
              <input id="ws-proj-step" value={step} onChange={e => setStep(e.target.value)} placeholder="Add a next step…" autoComplete="off" />
            </form>
            {done.map(n => (
              <div className="ws-todo ws-row done" key={n.id}>
                <button className="ws-check" role="checkbox" aria-checked={true} aria-label={`Put "${n.text}" back`} onClick={() => run(`/api/workspace/projects/${p.id}/next/${n.id}`, 'POST', { done: false })} />
                <span className="ws-todo-tt">{n.text}</span>
                <span />
              </div>
            ))}
          </div>
          {p.decided.length > 0 && (
            <>
              <h2 className="ws-sec-h">Decided</h2>
              <div className="ws-list">{p.decided.slice().reverse().map((d, i) => <div className="ws-row" key={i}><span className="ws-row-tt">{d.text}</span></div>)}</div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};
